// SPDX-License-Identifier: AGPL-3.0-or-later

const STEAM_APP_ID_PATTERN = /^\d{1,16}$/;
const STEAM_ICON_HASH_PATTERN = /^[a-f0-9]{40}$/i;
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g;
const ICON_DATA_URL_PATTERN = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/;
const MAX_ICON_DATA_URL_LENGTH = 360_000;

export const ActivityTypes = {
	PLAYING: 0,
} as const;

export type GatewayActivityPayload = Readonly<{
	type: 0;
	name: string;
	started_at?: number;
	application_id?: string;
}>;

export type GameActivityPresence = Readonly<{
	name: string;
	startedAtMs: number | null;
	steamAppId: string;
	steamIconHash?: string | null;
	iconDataUrl?: string | null;
}>;

export function sanitizeGameIconDataUrl(value: string | null | undefined): string | null {
	if (!value || value.length > MAX_ICON_DATA_URL_LENGTH) {
		return null;
	}
	return ICON_DATA_URL_PATTERN.test(value) ? value : null;
}

export function sanitizeDisplayText(value: string, maxLength: number): string {
	return value.replace(CONTROL_CHARS, '').trim().slice(0, maxLength);
}

export function isSteamAppId(value: string): boolean {
	return STEAM_APP_ID_PATTERN.test(value);
}

export function isSteamIconHash(value: string): boolean {
	return STEAM_ICON_HASH_PATTERN.test(value);
}

export function resolveSteamAppIdFromGameId(gameId: string, explicitSteamAppId?: string | null): string | null {
	if (explicitSteamAppId && isSteamAppId(explicitSteamAppId)) {
		return explicitSteamAppId;
	}
	if (!gameId.startsWith('steam-')) {
		return null;
	}
	const rest = gameId.slice('steam-'.length);
	const colonIndex = rest.indexOf(':');
	const appId = (colonIndex >= 0 ? rest.slice(0, colonIndex) : rest).trim();
	return isSteamAppId(appId) ? appId : null;
}

export function resolveSteamIconHashFromGameId(
	gameId: string,
	explicitSteamIconHash?: string | null,
): string | null {
	if (explicitSteamIconHash && isSteamIconHash(explicitSteamIconHash)) {
		return explicitSteamIconHash.toLowerCase();
	}
	if (!gameId.startsWith('steam-')) {
		return null;
	}
	const rest = gameId.slice('steam-'.length);
	const colonIndex = rest.indexOf(':');
	if (colonIndex < 0) {
		return null;
	}
	const hash = rest.slice(colonIndex + 1).trim();
	return isSteamIconHash(hash) ? hash.toLowerCase() : null;
}

export function getSteamCommunityIconUrl(steamAppId: string, iconHash: string): string | null {
	if (!isSteamAppId(steamAppId) || !isSteamIconHash(iconHash)) {
		return null;
	}
	return `https://steamcdn-a.akamaihd.net/steamcommunity/public/images/apps/${encodeURIComponent(steamAppId)}/${encodeURIComponent(iconHash.toLowerCase())}.jpg`;
}

export function getSteamCommunityIconCdnUrl(steamAppId: string, iconHash: string): string | null {
	if (!isSteamAppId(steamAppId) || !isSteamIconHash(iconHash)) {
		return null;
	}
	return `https://cdn.cloudflare.steamstatic.com/steamcommunity/public/images/apps/${encodeURIComponent(steamAppId)}/${encodeURIComponent(iconHash.toLowerCase())}.jpg`;
}

export function getSteamLibraryLogoUrl(steamAppId: string): string | null {
	if (!isSteamAppId(steamAppId)) {
		return null;
	}
	return `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${encodeURIComponent(steamAppId)}/logo.png`;
}

/**
 * Non-Steam games detected by the desktop app use reserved numeric ids (9100000000000000+) so they
 * travel through presence and game history like Steam games. Images are hosted by LafON.
 */
const LAFON_GAME_IMAGES: Readonly<Record<string, string>> = {
	'9100000000000001': 'https://api.lafon.com.tr/desktop/games/valorant.png',
	'9100000000000002': 'https://api.lafon.com.tr/desktop/games/league-of-legends.png',
};

export function getLafonGameImageUrl(gameId: string): string | null {
	return LAFON_GAME_IMAGES[gameId] ?? null;
}

/**
 * Community icon hashes for Steam games whose store assets have no legacy `logo.png`, so a presence
 * sent without a hash (older desktop builds) still gets an icon.
 */
const KNOWN_STEAM_ICON_HASHES: Readonly<Record<string, string>> = {
	'2807960': '3059e94fb89816fe1929a238ac2b55e8be348408', // Battlefield 6
	'3028330': '5aa046c38751386e05075e7a13257e52929633a5', // Battlefield REDSEC
};

export function getSteamIconUrlCandidates(steamAppId: string, steamIconHash?: string | null): Array<string> {
	const lafonImage = getLafonGameImageUrl(steamAppId);
	if (lafonImage) {
		return [lafonImage];
	}
	const knownHash = KNOWN_STEAM_ICON_HASHES[steamAppId] ?? null;
	const urls: Array<string> = [];
	// Newer apps only serve community icons from the `community_assets` path; the legacy CDN paths 404.
	for (const hash of [steamIconHash, knownHash]) {
		if (hash && isSteamIconHash(hash) && isSteamAppId(steamAppId)) {
			const url = `https://shared.fastly.steamstatic.com/community_assets/images/apps/${encodeURIComponent(steamAppId)}/${encodeURIComponent(hash.toLowerCase())}.jpg`;
			if (!urls.includes(url)) urls.push(url);
		}
	}
	steamIconHash ||= knownHash;
	if (steamIconHash) {
		const community = getSteamCommunityIconUrl(steamAppId, steamIconHash);
		if (community) {
			urls.push(community);
		}
		const cdn = getSteamCommunityIconCdnUrl(steamAppId, steamIconHash);
		if (cdn && !urls.includes(cdn)) {
			urls.push(cdn);
		}
	}
	const logo = getSteamLibraryLogoUrl(steamAppId);
	if (logo && !urls.includes(logo)) {
		urls.push(logo);
	}
	return urls;
}

function buildGatewayApplicationId(activity: GameActivityPresence): string | undefined {
	const hash = activity.steamIconHash && isSteamIconHash(activity.steamIconHash) ? activity.steamIconHash.toLowerCase() : '';
	const base = hash ? `steam-${activity.steamAppId}:${hash}` : `steam-${activity.steamAppId}`;
	return base.length <= 64 ? base : `steam-${activity.steamAppId}`.slice(0, 64);
}

export function toGatewayActivities(activity: GameActivityPresence | null): Array<GatewayActivityPayload> | null {
	if (!activity) {
		return null;
	}
	const name = sanitizeDisplayText(activity.name, 128);
	if (!name || !isSteamAppId(activity.steamAppId)) {
		return null;
	}
	const applicationId = buildGatewayApplicationId(activity);
	return [
		{
			type: ActivityTypes.PLAYING,
			name,
			...(activity.startedAtMs != null && activity.startedAtMs > 0
				? {started_at: Math.floor(activity.startedAtMs / 1000)}
				: {}),
			...(applicationId ? {application_id: applicationId} : {}),
		},
	];
}

export function fromGatewayActivities(
	activities: ReadonlyArray<GatewayActivityPayload> | null | undefined,
): GameActivityPresence | null {
	if (!activities?.length) {
		return null;
	}
	const playing = activities.find(
		(activity) => Number(activity.type) === ActivityTypes.PLAYING && typeof activity.name === 'string',
	);
	if (!playing?.name) {
		return null;
	}
	const name = sanitizeDisplayText(playing.name, 128);
	const applicationId =
		typeof playing.application_id === 'string' ? playing.application_id.trim() : '';
	if (!applicationId.startsWith('steam-')) {
		return null;
	}
	const steamAppId = resolveSteamAppIdFromGameId(applicationId, null);
	if (!name || !steamAppId) {
		return null;
	}
	const startedAtMs =
		typeof playing.started_at === 'number' && playing.started_at > 0 ? playing.started_at * 1000 : null;
	return {
		name,
		startedAtMs,
		steamAppId,
		steamIconHash: resolveSteamIconHashFromGameId(applicationId, null),
	};
}

export function gameActivityToKey(activity: GameActivityPresence | null): string {
	if (!activity) {
		return '';
	}
	return `${activity.name}|${activity.startedAtMs ?? ''}|${activity.steamAppId}|${activity.steamIconHash ?? ''}|${activity.iconDataUrl ? '1' : ''}`;
}

export function reuseGameActivitySnapshot(
	previous: GameActivityPresence | null,
	next: GameActivityPresence | null,
): GameActivityPresence | null {
	return gameActivityToKey(previous) === gameActivityToKey(next) ? previous : next;
}

export function formatGamePlayDuration(elapsedMs: number): string {
	const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	if (hours > 0) {
		return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
	}
	return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
